import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ImagePlus, Pencil, Star, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

const STATUS_LABEL = { pendente: "Pendente", enviando: "Enviando...", ok: "Enviada", erro: "Erro" };

export function AlbumDetail({ album, onClose, onChanged }) {
  const [fotos, setFotos] = useState([]);
  const [queue, setQueue] = useState([]);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(null);
  const [photoForm, setPhotoForm] = useState({ titulo: "", legenda: "", ano: "" });
  const [lightbox, setLightbox] = useState(null);
  const [linkedMap, setLinkedMap] = useState({});
  const [busyDestaque, setBusyDestaque] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/albums/${album.id}`);
    const fotosData = data.fotos || [];
    setFotos(fotosData);
    if (!fotosData.length) {
      setLinkedMap({});
      return;
    }
    try {
      const mapRes = await api.get("/site-content/memory-photos/linked-map", {
        params: { ids: fotosData.map((f) => f.id).join(",") },
      });
      setLinkedMap(mapRes.data || {});
    } catch {
      setLinkedMap({});
    }
  }, [album.id]);

  const toggleDestaque = async (foto) => {
    const memId = linkedMap[foto.id];
    setBusyDestaque(foto.id);
    try {
      if (memId) {
        await api.delete(`/site-content/memory-photos/${memId}`);
        toast.success("Destaque removido das Memórias — a foto continua no álbum.");
      } else {
        await api.post("/site-content/memory-photos/from-album", { album_photo_id: foto.id });
        toast.success("Foto destacada nas Memórias do IMJ.");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyDestaque(null);
    }
  };
  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, [load]);

  const pick = (e) => {
    const novos = [...(e.target.files || [])].map((file) =>
      file.size > 10 * 1024 * 1024
        ? { id: crypto.randomUUID(), file, preview: null, status: "erro", erro: "Excede 10 MB" }
        : { id: crypto.randomUUID(), file, preview: URL.createObjectURL(file), status: "pendente", erro: null }
    );
    setQueue((q) => [...q, ...novos]);
    e.target.value = "";
  };

  const setStatus = (id, status, erro = null) =>
    setQueue((q) => q.map((i) => (i.id === id ? { ...i, status, erro } : i)));

  const send = async () => {
    setSending(true);
    let ok = 0;
    let falhas = 0;
    for (const item of queue.filter((i) => i.status === "pendente")) {
      setStatus(item.id, "enviando");
      try {
        const fd = new FormData();
        fd.append("files", item.file);
        await api.post(`/albums/${album.id}/photos`, fd);
        setStatus(item.id, "ok");
        ok += 1;
      } catch (err) {
        setStatus(item.id, "erro", formatApiError(err));
        falhas += 1;
      }
    }
    setSending(false);
    if (ok) {
      toast.success(`${ok} foto(s) enviada(s) para o álbum.`);
      load();
      onChanged();
    }
    if (falhas) toast.error(`${falhas} foto(s) com erro — as demais foram enviadas.`);
    setQueue((q) => q.filter((i) => i.status === "erro"));
  };

  const move = async (foto, dir) => {
    const idx = fotos.findIndex((f) => f.id === foto.id);
    const alvo = idx + dir;
    if (alvo < 0 || alvo >= fotos.length) return;
    const nova = [...fotos];
    [nova[idx], nova[alvo]] = [nova[alvo], nova[idx]];
    setFotos(nova);
    try {
      await api.put(`/albums/${album.id}/photos/reorder`, { ids: nova.map((f) => f.id) });
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };

  const openEdit = (foto) => {
    setEditing(foto);
    setPhotoForm({ titulo: foto.titulo || "", legenda: foto.legenda || "", ano: foto.ano || "" });
  };

  const savePhoto = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/albums/${album.id}/photos/${editing.id}`, photoForm);
      toast.success("Foto atualizada.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removePhoto = async (foto) => {
    try {
      await api.delete(`/albums/${album.id}/photos/${foto.id}`);
      toast.success("Foto removida do álbum.");
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pendentes = queue.filter((i) => i.status === "pendente").length;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto" data-testid="album-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {album.titulo}
            <Badge variant={album.published ? "default" : "secondary"} className={album.published ? "bg-primary text-primary-foreground" : ""}>
              {album.published ? "Publicado" : "Não publicado"}
            </Badge>
          </DialogTitle>
          <DialogDescription className="sr-only">Gerenciamento das fotos do álbum, incluindo destaque nas Memórias do IMJ.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 border border-dashed border-border p-4" data-testid="album-upload-area">
          <div className="flex flex-wrap items-center gap-2">
            <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="album-photo-file" />
            <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={sending} data-testid="album-photo-pick">
              <ImagePlus size={14} className="mr-1" /> Selecionar fotos
            </Button>
            {pendentes > 0 && (
              <Button type="button" size="sm" onClick={send} disabled={sending} className="bg-primary text-primary-foreground" data-testid="album-photo-send">
                {sending ? "Enviando..." : `Enviar ${pendentes} foto(s)`}
              </Button>
            )}
            <p className="text-xs text-muted-foreground">JPEG, PNG ou WebP · máx. 10 MB cada · otimização automática.</p>
          </div>
          {queue.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {queue.map((item) => (
                <div key={item.id} className="relative w-24" data-testid={`album-queue-${item.id}`}>
                  {item.preview ? (
                    <img src={item.preview} alt="" className="h-20 w-24 border border-border object-cover" />
                  ) : (
                    <div className="flex h-20 w-24 items-center justify-center border border-border bg-secondary text-[10px] text-muted-foreground">sem preview</div>
                  )}
                  <span className={`mt-1 block truncate text-[10px] ${item.status === "erro" ? "text-destructive" : "text-muted-foreground"}`}>
                    {STATUS_LABEL[item.status]}{item.erro ? ` — ${item.erro}` : ""}
                  </span>
                  {item.status !== "enviando" && (
                    <button type="button" className="absolute -right-1 -top-1 border border-border bg-white p-0.5" onClick={() => setQueue((q) => q.filter((i) => i.id !== item.id))} data-testid={`album-queue-remove-${item.id}`}>
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {fotos.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground" data-testid="album-photos-empty">Nenhuma foto neste álbum ainda.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="album-photos-grid">
            {fotos.map((foto, idx) => (
              <div key={foto.id} className="border border-border" data-testid={`album-photo-${foto.id}`}>
                <button type="button" className="block w-full" onClick={() => setLightbox(foto)} data-testid={`album-photo-view-${foto.id}`}>
                  <img src={foto.url} alt={foto.titulo || "Foto do álbum"} className="aspect-square w-full object-cover" loading="lazy" />
                </button>
                <div className="space-y-1 p-2">
                  <p className="truncate text-xs font-medium">{foto.titulo || "Sem título"}{foto.ano ? ` · ${foto.ano}` : ""}</p>
                  {linkedMap[foto.id] && (
                    <Badge className="bg-primary text-[10px] text-primary-foreground" data-testid={`album-photo-destaque-badge-${foto.id}`}>
                      Em destaque nas Memórias
                    </Badge>
                  )}
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" disabled={idx === 0} onClick={() => move(foto, -1)} data-testid={`album-photo-left-${foto.id}`}><ArrowLeft size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" disabled={idx === fotos.length - 1} onClick={() => move(foto, 1)} data-testid={`album-photo-right-${foto.id}`}><ArrowRight size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(foto)} data-testid={`album-photo-edit-${foto.id}`}><Pencil size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removePhoto(foto)} data-testid={`album-photo-delete-${foto.id}`}><Trash2 size={13} className="text-destructive" /></Button>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-[11px]"
                    disabled={busyDestaque === foto.id}
                    onClick={() => toggleDestaque(foto)}
                    data-testid={`album-photo-destaque-${foto.id}`}
                  >
                    <Star size={12} className={`mr-1 ${linkedMap[foto.id] ? "fill-primary text-primary" : ""}`} />
                    {busyDestaque === foto.id ? "Processando..." : linkedMap[foto.id] ? "Remover das Memórias" : "Destacar nas Memórias"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
          <DialogContent data-testid="album-photo-dialog">
            <DialogHeader>
              <DialogTitle>Editar foto</DialogTitle>
              <DialogDescription className="sr-only">Edição de título, legenda e ano da foto do álbum.</DialogDescription>
            </DialogHeader>
            <form onSubmit={savePhoto} className="space-y-4">
              <div className="space-y-2">
                <Label>Título</Label>
                <Input value={photoForm.titulo} onChange={(e) => setPhotoForm({ ...photoForm, titulo: e.target.value })} data-testid="album-photo-titulo-input" />
              </div>
              <div className="space-y-2">
                <Label>Legenda</Label>
                <Input value={photoForm.legenda} onChange={(e) => setPhotoForm({ ...photoForm, legenda: e.target.value })} data-testid="album-photo-legenda-input" />
              </div>
              <div className="space-y-2">
                <Label>Ano</Label>
                <Input value={photoForm.ano} onChange={(e) => setPhotoForm({ ...photoForm, ano: e.target.value })} placeholder="Ex.: 2011" data-testid="album-photo-ano-input" />
              </div>
              <Button type="submit" className="w-full bg-primary text-primary-foreground" data-testid="album-photo-save">Salvar</Button>
            </form>
          </DialogContent>
        </Dialog>

        {lightbox && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4" onClick={() => setLightbox(null)} data-testid="album-lightbox">
            <img src={lightbox.url} alt={lightbox.titulo || "Foto ampliada"} className="max-h-[85vh] max-w-full object-contain" />
            <button type="button" className="absolute right-4 top-4 text-white" onClick={() => setLightbox(null)} data-testid="album-lightbox-close"><X size={26} /></button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
